/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Controller, useForm } from "react-hook-form";
import { When } from "react-if";
import { FlexContainer, Description, Select } from "@nordcloud/gnui";
import { MixedValue } from "../types";
import { EditButtons } from "./EditButtons";
import { InlineLoading } from "./InlineLoading";
import { InlineEditProps, InlineSelectForm } from "./types";

export function InlineSelect({
  selectOptions,
  updateHook,
  property,
  value,
  onClose,
  onSuccess,
  minWidth,
}: InlineEditProps) {
  const {
    control,
    handleSubmit,

    formState: { errors },
  } = useForm();

  const [update, mutationLoading] = updateHook({
    onSuccess: () => {
      onClose();
      onSuccess?.();
    },
  });
  // Creating new object because Select component returns whole option object, not only its value property.
  const onEdit = (values: InlineSelectForm) => {
    const propertyValues = values[property];
    const val = Array.isArray(propertyValues)
      ? propertyValues.map((item) => item.value)
      : propertyValues.value;

    update({
      [property]: val,
    });
  };

  if (mutationLoading) {
    return <InlineLoading />;
  }

  const showError = errors[property] !== undefined;

  return (
    <form onSubmit={handleSubmit(onEdit)}>
      <FlexContainer css={{ minHeight: "2.5rem" }}>
        <Controller
          control={control}
          name={property}
          render={({ field: { onChange, value: selectedValue } }) => {
            const getValue = (val: MixedValue, selectedVal: MixedValue) => {
              if (Array.isArray(val)) {
                if (selectedVal) {
                  return selectedVal;
                }
                return val?.map((name: string) => ({
                  value: name,
                  label: name,
                }));
              }
              if (!Array.isArray(val)) {
                if (selectedVal) {
                  return selectedVal;
                }

                return { value: val, label: val };
              }
            };
            return (
              <div css={{ width: minWidth }}>
                <Select
                  isMulti={Array.isArray(value)}
                  name="Select"
                  value={getValue(value, selectedValue)}
                  onChange={onChange}
                  options={selectOptions ?? []}
                />
              </div>
            );
          }}
        />
        <EditButtons onClose={onClose} />
      </FlexContainer>
      <When condition={showError}>
        <Description>This field is required.</Description>
      </When>
    </form>
  );
}
